import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Lock, Check, MapPin, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";
import { JOURNEY as STAGES } from "../lib/journey";
import { useJourney } from "../context/JourneyContext";

const Card = ({ s, align, locked, done, current }) => {
  const right = align === "right";
  const inner = (
    <motion.div
      whileHover={locked ? {} : { y: -6 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden rounded-sm border bg-navy-soft/30 p-8 backdrop-blur-sm transition-colors duration-300 ${
        locked ? "border-gold/10 opacity-60" : "border-gold/20 hover:border-gold/50"
      } ${right ? "md:text-right" : ""}`}
    >
      <div className={`flex items-center gap-4 ${right ? "md:flex-row-reverse" : ""}`}>
        <span
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full border transition-colors duration-300 ${
            done ? "border-gold bg-gold text-navy" : "border-gold/40 text-gold group-hover:bg-gold group-hover:text-navy"
          }`}
        >
          {locked ? <Lock size={18} /> : <s.icon size={20} />}
        </span>
        <div className={`flex items-baseline gap-3 ${right ? "md:flex-row-reverse" : ""}`}>
          <span className="text-[0.65rem] uppercase tracking-luxe text-gold/60">Nivel 0{s.n}</span>
          <h3 className="font-serif text-3xl font-normal text-cream">{s.title}</h3>
        </div>
      </div>

      <p className="mt-4 text-sm font-light leading-relaxed text-cream/70">{s.desc}</p>

      <div className={`mt-6 flex items-center gap-2 text-sm font-medium ${right ? "md:justify-end" : ""}`}>
        {locked ? (
          <span className="flex items-center gap-2 text-cream/50">
            <Lock size={14} /> Se desbloquea al completar la etapa anterior
          </span>
        ) : done ? (
          <span className="flex items-center gap-2 text-gold">
            <Check size={15} /> Etapa completada
          </span>
        ) : (
          <span className="flex items-center gap-2 text-gold">
            {current && <MapPin size={14} />}
            {current ? "Estás aquí · continuar" : "Desbloquear este nivel"}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        )}
      </div>
    </motion.div>
  );

  return locked ? (
    <div data-testid={`journey-stage-${s.n}`}>{inner}</div>
  ) : (
    <Link to={s.to} className="block" data-testid={`journey-stage-${s.n}`}>
      {inner}
    </Link>
  );
};

export const Journey = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 65%", "end 60%"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);
  const { isUnlocked, completed, currentStage, openAssessment } = useJourney();

  return (
    <section id="camino" className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="journey-section">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/3 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-gold/5 blur-[160px]" />
      </div>

      <div className="container-luxe relative z-10">
        <div className="mb-20 max-w-2xl">
          <Reveal>
            <p className="overline mb-6 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> El recorrido
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
              Tu camino <span className="italic text-gold">comienza aquí</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base font-light leading-relaxed text-cream/70 sm:text-lg">
              No llegarás a una colección de cursos. Comenzarás un recorrido diseñado para ayudarte a
              comprender tu historia y desarrollar una vida más consciente.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <button onClick={openAssessment} className="btn-gold mt-8" data-testid="journey-assessment-cta">
              <Sparkles size={15} /> Comienza tu viaje
            </button>
          </Reveal>
        </div>

        {/* timeline */}
        <div ref={ref} className="relative">
          {/* connecting golden line */}
          <div className="absolute left-6 top-0 h-full w-px bg-gold/15 md:left-1/2 md:-translate-x-1/2">
            <motion.div style={{ scaleY }} className="h-full w-full origin-top bg-gold" />
          </div>

          <div className="flex flex-col gap-12 md:gap-16">
            {STAGES.map((s, i) => {
              const isLeft = i % 2 === 0;
              const locked = !isUnlocked(s.n);
              const done = completed.includes(s.n);
              const current = currentStage?.n === s.n;
              return (
                <div key={s.n} className="relative grid grid-cols-1 items-center md:grid-cols-2 md:gap-x-16">
                  {/* node */}
                  <span className="absolute left-6 top-8 z-10 -translate-x-1/2 md:left-1/2 md:top-1/2 md:-translate-y-1/2">
                    <span className={`block h-4 w-4 rounded-full border bg-navy ${locked ? "border-gold/30" : "border-gold"}`}>
                      {!locked && <span className="block h-full w-full scale-50 rounded-full bg-gold animate-pulse-ring" />}
                    </span>
                  </span>

                  {isLeft ? (
                    <>
                      <Reveal className="pl-16 md:pl-0 md:pr-4" y={40}>
                        <Card s={s} align="right" locked={locked} done={done} current={current} />
                      </Reveal>
                      <div className="hidden md:block" />
                    </>
                  ) : (
                    <>
                      <div className="hidden md:block" />
                      <Reveal className="pl-16 md:pl-4" y={40}>
                        <Card s={s} align="left" locked={locked} done={done} current={current} />
                      </Reveal>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.1}>
          <p className="mx-auto mt-24 max-w-xl text-center font-serif text-3xl font-light leading-snug text-cream sm:text-4xl">
            No estás comenzando un curso.
            <br />
            Estás comenzando <span className="italic text-gold">un viaje</span>.
          </p>
        </Reveal>
      </div>
    </section>
  );
};
